import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACCESS_LEVELS,
  ACCESS_LEVEL_LABELS,
  ACCESS_REVIEW_DECISION_LABELS,
  ACCESS_REVIEW_OUTCOMES,
  ACCESS_REVIEW_OUTCOME_LABELS,
  ACCESS_REVIEW_STATUS_LABELS,
  type AccessLevel,
  type AccessReview,
  type AccessReviewDecision,
  type AccessReviewItem,
  type AccessReviewOutcome,
  type AccessReviewSystem,
  type DecideAccessReviewItemInput,
  type UpsertAccessReviewSystemInput,
} from '@grc/types';
import { apiFetch, apiUrl } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  ProgressBar,
  Select,
  Table,
  Textarea,
  formatDate,
  useToast,
  type BadgeTone,
} from '../../components/ui.js';
import {
  REVIEW_STATUS_TONE,
  accessLevelLabel,
  addMonthsIso,
  apiErrorMessage,
  localDateIso,
  reviewsKey,
  scopeLabel,
} from './AccessReviewShared.js';
import './records.css';

const DECISION_TONE: Record<AccessReviewDecision, BadgeTone> = {
  pending: 'neutral',
  confirmed: 'success',
  modified: 'warning',
  revoked: 'danger',
};

const OUTCOME_TONE: Record<AccessReviewOutcome, BadgeTone> = {
  compliant: 'success',
  compliant_actions_open: 'warning',
  nonconformity: 'danger',
};

const REPORT_FORMATS = [
  { format: 'html', label: 'HTML' },
  { format: 'pdf', label: 'PDF' },
  { format: 'docx', label: 'DOCX' },
  { format: 'markdown', label: 'Markdown' },
] as const;

interface SystemGroup {
  recordId: string;
  name: string;
  deleted: boolean;
  items: AccessReviewItem[];
}

/** One access review (/assets/software/access-reviews/:reviewId). Admins and reviewers only. */
export function AccessReviewDetailPage() {
  const { reviewId = '' } = useParams<{ reviewId: string }>();
  const { org } = useOrg();
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isSystemAdmin = me?.user.isSystemAdmin ?? false;
  const isAdmin = org.role === 'admin' || isSystemAdmin;
  const allowed = isAdmin || org.role === 'reviewer';

  const review = useQuery({
    queryKey: [...reviewsKey(org.orgId), 'detail', reviewId],
    queryFn: () => apiFetch<AccessReview>(`/orgs/${org.orgId}/access-reviews/${reviewId}`),
    enabled: allowed && reviewId !== '',
  });
  const itemsKey = [...reviewsKey(org.orgId), 'items', reviewId];
  const items = useQuery({
    queryKey: itemsKey,
    queryFn: () =>
      apiFetch<{ items: AccessReviewItem[] }>(
        `/orgs/${org.orgId}/access-reviews/${reviewId}/items`,
      ),
    enabled: allowed && reviewId !== '',
  });

  const [completeOpen, setCompleteOpen] = useState(false);
  const [systemDialog, setSystemDialog] = useState<{
    open: boolean;
    system: AccessReviewSystem | null;
  }>({ open: false, system: null });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: reviewsKey(org.orgId) });
  };
  const onError = (fallback: string) => (err: unknown) => toast(apiErrorMessage(err, fallback), 'error');

  const decide = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: DecideAccessReviewItemInput }) =>
      apiFetch<AccessReviewItem>(
        `/orgs/${org.orgId}/access-reviews/${reviewId}/items/${itemId}/decide`,
        { method: 'POST', body },
      ),
    onSuccess: (updated) => {
      // Swap the item in place so the table does not flicker, then refresh the counts.
      queryClient.setQueryData<{ items: AccessReviewItem[] }>(itemsKey, (old) =>
        old ? { items: old.items.map((i) => (i.id === updated.id ? updated : i)) } : old,
      );
      void queryClient.invalidateQueries({ queryKey: [...reviewsKey(org.orgId), 'detail', reviewId] });
      void queryClient.invalidateQueries({ queryKey: [...reviewsKey(org.orgId), 'list'] });
    },
    onError: onError('Could not save the decision'),
  });

  const complete = useMutation({
    mutationFn: (body: { notes: string; nextReviewOn: string }) =>
      apiFetch<AccessReview>(`/orgs/${org.orgId}/access-reviews/${reviewId}/complete`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      setCompleteOpen(false);
      toast('Review completed and the decisions applied to the grants.', 'success');
      invalidate();
      // Grants changed: refresh assignment lists and the overdue panel.
      void queryClient.invalidateQueries({ queryKey: ['record-assignments', org.orgId] });
    },
    onError: onError('Could not complete the review'),
  });

  const approve = useMutation({
    mutationFn: () =>
      apiFetch<AccessReview>(`/orgs/${org.orgId}/access-reviews/${reviewId}/approve`, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast('Review approved.', 'success');
      invalidate();
    },
    onError: onError('Could not approve the review'),
  });

  const remove = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true }>(`/orgs/${org.orgId}/access-reviews/${reviewId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      toast('Review deleted.', 'success');
      invalidate();
      navigate('/assets/software/access-reviews');
    },
    onError: onError('Could not delete the review'),
  });

  const saveSystem = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: UpsertAccessReviewSystemInput }) =>
      id
        ? apiFetch<AccessReviewSystem>(
            `/orgs/${org.orgId}/access-reviews/${reviewId}/systems/${id}`,
            { method: 'PATCH', body: input },
          )
        : apiFetch<AccessReviewSystem>(`/orgs/${org.orgId}/access-reviews/${reviewId}/systems`, {
            method: 'POST',
            body: input,
          }),
    onSuccess: (_row, { id }) => {
      setSystemDialog({ open: false, system: null });
      toast(id ? 'System updated.' : 'System added.', 'success');
      invalidate();
    },
    onError: onError('Could not save the system'),
  });

  const removeSystem = useMutation({
    mutationFn: (systemId: string) =>
      apiFetch<{ ok: true }>(
        `/orgs/${org.orgId}/access-reviews/${reviewId}/systems/${systemId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      toast('System removed.', 'success');
      invalidate();
    },
    onError: onError('Could not remove the system'),
  });

  const list = useMemo(() => items.data?.items ?? [], [items.data]);
  const groups = useMemo<SystemGroup[]>(() => {
    const map = new Map<string, SystemGroup>();
    for (const item of list) {
      const group = map.get(item.recordId) ?? {
        recordId: item.recordId,
        name: item.recordName,
        deleted: item.recordDeleted,
        items: [],
      };
      group.items.push(item);
      map.set(item.recordId, group);
    }
    return [...map.values()];
  }, [list]);

  if (!allowed) {
    return (
      <EmptyState
        title="Admins and reviewers only"
        message="Access reviews list colleagues' access levels and decisions, so only organisation admins and reviewers can open them."
      />
    );
  }
  if (review.isLoading || items.isLoading) return <LoadingPane label="Loading the review" />;
  const r = review.data;
  if (!r) {
    return (
      <EmptyState
        title="Review not found"
        message="This access review does not exist or belongs to another organisation."
        action={
          <Link to="/assets/software/access-reviews">
            <Button variant="secondary">All access reviews</Button>
          </Link>
        }
      />
    );
  }

  const inProgress = r.status === 'in_progress';
  const canAct = isAdmin || r.reviewerId === me?.user.id;
  const decided = r.counts.total - r.counts.pending;
  const percent = r.counts.total === 0 ? 0 : Math.round((decided / r.counts.total) * 100);
  const revocations = list.filter((i) => i.decision === 'revoked');
  const modifications = list.filter((i) => i.decision === 'modified');

  return (
    <div className="review-page">
      <PageHeader
        title={r.name}
        subtitle={
          <>
            {scopeLabel(r)} · Reviewer {r.reviewerName} · Started {formatDate(r.createdAt)}
            {r.completedAt && ` · Completed ${formatDate(r.completedAt)}`}
            {r.approvedAt && ` · Approved by ${r.approverName ?? '—'} ${formatDate(r.approvedAt)}`}
            {r.nextReviewOn && ` · Next review ${formatDate(r.nextReviewOn)}`}
          </>
        }
        actions={
          <>
            <Badge tone={REVIEW_STATUS_TONE[r.status]}>{ACCESS_REVIEW_STATUS_LABELS[r.status]}</Badge>
            <Link to="/assets/software/access-reviews">
              <Button variant="secondary">All access reviews</Button>
            </Link>
          </>
        }
      />

      {r.notes && <p className="coverage-hint">{r.notes}</p>}
      {inProgress && !canAct && (
        <p className="coverage-hint">
          Only {r.reviewerName} or an organisation admin can decide these grants and complete the
          review.
        </p>
      )}

      {groups.map((group) => (
        <section key={group.recordId} className="review-group" aria-label={group.name}>
          <h2 className="review-group-title">
            {group.name}
            {group.deleted && <Badge tone="neutral">record deleted</Badge>}
            <span className="review-group-count">
              {group.items.length} grant{group.items.length === 1 ? '' : 's'}
            </span>
          </h2>
          <Table
            head={
              <>
                <th>Person</th>
                <th>Email</th>
                <th>Access level</th>
                <th>Granted</th>
                <th>Approved by</th>
                <th>Decision</th>
                <th>Decided by</th>
              </>
            }
          >
            {group.items.map((item) => (
              <tr key={item.id}>
                <td style={{ fontWeight: 600 }}>{item.userName}</td>
                <td>{item.userEmail}</td>
                <td>
                  {accessLevelLabel(item.accessLevel)}
                  {item.accessRole && <span className="cell-muted">{item.accessRole}</span>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(item.grantedOn)}</td>
                <td>{item.approvedByName ?? '—'}</td>
                <td>
                  {inProgress && canAct ? (
                    <DecisionControls
                      item={item}
                      saving={decide.isPending && decide.variables?.itemId === item.id}
                      onDecide={(body) => decide.mutate({ itemId: item.id, body })}
                    />
                  ) : (
                    <DecisionSummary item={item} />
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {item.decidedByName ? (
                    <>
                      {item.decidedByName}
                      <span className="cell-muted">{formatDate(item.decidedAt)}</span>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </section>
      ))}

      <Card
        title="Other systems reviewed"
        actions={
          inProgress ? (
            <Button size="sm" onClick={() => setSystemDialog({ open: true, system: null })}>
              Add system
            </Button>
          ) : undefined
        }
      >
        <p className="coverage-hint">
          Systems that are not per-person software grants — shared mailboxes, client environments,
          physical access, banking — recorded here appear in the report&rsquo;s summary alongside the
          grants above.
        </p>
        {r.systems.length === 0 ? (
          <p className="panel-empty">No other systems recorded.</p>
        ) : (
          <Table
            head={
              <>
                <th>System</th>
                <th>Accounts reviewed</th>
                <th>Privileged roles</th>
                <th>Changes made</th>
                <th>Outcome</th>
                {inProgress && <th />}
              </>
            }
          >
            {r.systems.map((s) => (
              <tr key={s.id}>
                <td style={{ fontWeight: 600 }}>{s.system}</td>
                <td className="review-system-text">{s.accountsReviewed || '—'}</td>
                <td className="review-system-text">{s.privilegedRoles || '—'}</td>
                <td className="review-system-text">{s.changesMade || '—'}</td>
                <td>
                  <Badge tone={OUTCOME_TONE[s.outcome]}>{ACCESS_REVIEW_OUTCOME_LABELS[s.outcome]}</Badge>
                </td>
                {inProgress && (
                  <td>
                    <div className="record-row-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Edit ${s.system}`}
                        onClick={() => setSystemDialog({ open: true, system: s })}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Remove ${s.system}`}
                        loading={removeSystem.isPending && removeSystem.variables === s.id}
                        onClick={() => {
                          if (window.confirm(`Remove ${s.system} from this review?`)) {
                            removeSystem.mutate(s.id);
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <footer className="review-footer" aria-label="Review progress and actions">
        <div className="review-footer-progress">
          <ProgressBar percent={percent} label="Grants decided" />
          <span className="review-footer-counts">
            {decided} of {r.counts.total} decided · {r.counts.confirmed} confirmed ·{' '}
            {r.counts.modified} modified · {r.counts.revoked} revoked
          </span>
        </div>
        <div className="review-footer-actions">
          <span className="review-footer-label">Report</span>
          {REPORT_FORMATS.map((f) => (
            <a
              key={f.format}
              href={apiUrl(`/orgs/${org.orgId}/access-reviews/${reviewId}/report?format=${f.format}`)}
            >
              <Button variant="secondary" size="sm">
                {f.label}
              </Button>
            </a>
          ))}
          {inProgress && isAdmin && (
            <Button
              variant="danger"
              size="sm"
              loading={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete ${r.name}? Its decisions so far will be lost.`)) {
                  remove.mutate();
                }
              }}
            >
              Delete
            </Button>
          )}
          {r.status === 'completed' && isAdmin && (
            <Button loading={approve.isPending} onClick={() => approve.mutate()}>
              Approve
            </Button>
          )}
          {inProgress && canAct && (
            <Button
              disabled={r.counts.pending > 0 || r.counts.total === 0}
              title={r.counts.pending > 0 ? 'Decide every grant first' : undefined}
              onClick={() => setCompleteOpen(true)}
            >
              Complete review
            </Button>
          )}
        </div>
      </footer>

      <CompleteDialog
        open={completeOpen}
        review={r}
        revocations={revocations}
        modifications={modifications}
        saving={complete.isPending}
        onClose={() => setCompleteOpen(false)}
        onComplete={(body) => complete.mutate(body)}
      />
      <SystemDialog
        open={systemDialog.open}
        system={systemDialog.system}
        saving={saveSystem.isPending}
        onClose={() => setSystemDialog({ open: false, system: null })}
        onSave={(input) => saveSystem.mutate({ id: systemDialog.system?.id ?? null, input })}
      />
    </div>
  );
}

/** Read-only decision (after completion, or for someone who cannot decide). */
function DecisionSummary({ item }: { item: AccessReviewItem }) {
  return (
    <>
      <Badge tone={DECISION_TONE[item.decision]}>
        {ACCESS_REVIEW_DECISION_LABELS[item.decision]}
        {item.decision === 'modified' &&
          item.newAccessLevel &&
          ` → ${ACCESS_LEVEL_LABELS[item.newAccessLevel]}`}
      </Badge>
      {item.note && <span className="cell-muted">{item.note}</span>}
    </>
  );
}

/**
 * Confirm / Modify / Revoke as a button group (ui.tsx has no segmented
 * control), the new-level select once Modify is chosen, and a note that is
 * saved on blur with the current decision.
 */
function DecisionControls({
  item,
  saving,
  onDecide,
}: {
  item: AccessReviewItem;
  saving: boolean;
  onDecide: (body: DecideAccessReviewItemInput) => void;
}) {
  const [modifying, setModifying] = useState(item.decision === 'modified');
  const [note, setNote] = useState(item.note);
  useEffect(() => setNote(item.note), [item.note]);
  useEffect(() => {
    if (item.decision === 'modified') setModifying(true);
  }, [item.decision]);

  const send = (decision: DecideAccessReviewItemInput['decision'], newAccessLevel?: AccessLevel) =>
    onDecide({ decision, newAccessLevel, note: note.trim() });
  const showLevel = modifying || item.decision === 'modified';

  // A decision button must not take focus off the note first: that blur saves
  // the note on its own, the row re-renders with the buttons disabled while it
  // is in flight, and the browser then never dispatches the click. Keeping the
  // focus where it is costs nothing — send() already carries the typed note.
  const keepFocus = (e: MouseEvent<HTMLButtonElement>) => e.preventDefault();

  return (
    <div className="decision-cell">
      <div className="decision-group" role="group" aria-label={`Decision for ${item.userName}`}>
        <Button
          size="sm"
          variant={item.decision === 'confirmed' ? 'primary' : 'secondary'}
          aria-pressed={item.decision === 'confirmed'}
          disabled={saving}
          onMouseDown={keepFocus}
          onClick={() => {
            setModifying(false);
            send('confirmed');
          }}
        >
          Confirm
        </Button>
        <Button
          size="sm"
          variant={item.decision === 'modified' ? 'primary' : 'secondary'}
          aria-pressed={item.decision === 'modified'}
          disabled={saving}
          onMouseDown={keepFocus}
          onClick={() => setModifying(true)}
        >
          Modify
        </Button>
        <Button
          size="sm"
          variant={item.decision === 'revoked' ? 'danger' : 'secondary'}
          aria-pressed={item.decision === 'revoked'}
          disabled={saving}
          onMouseDown={keepFocus}
          onClick={() => {
            setModifying(false);
            send('revoked');
          }}
        >
          Revoke
        </Button>
        {item.decision !== 'pending' && (
          <Button
            size="sm"
            variant="ghost"
            disabled={saving}
            aria-label={`Undo the decision for ${item.userName}`}
            onMouseDown={keepFocus}
            onClick={() => {
              setModifying(false);
              send('pending');
            }}
          >
            Undo
          </Button>
        )}
      </div>
      {showLevel && (
        <Select
          aria-label={`New access level for ${item.userName}`}
          value={item.decision === 'modified' ? (item.newAccessLevel ?? '') : ''}
          disabled={saving}
          onChange={(e) => {
            const level = e.target.value as AccessLevel | '';
            if (level) send('modified', level);
          }}
        >
          <option value="">Choose the new level…</option>
          {ACCESS_LEVELS.filter((level) => level !== item.accessLevel).map((level) => (
            <option key={level} value={level}>
              {ACCESS_LEVEL_LABELS[level]}
            </option>
          ))}
        </Select>
      )}
      <Input
        aria-label={`Note for ${item.userName}`}
        placeholder="Note (optional)"
        value={note}
        maxLength={2000}
        disabled={saving}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          if (note.trim() !== item.note) {
            onDecide({
              decision: item.decision,
              newAccessLevel: item.newAccessLevel ?? undefined,
              note: note.trim(),
            });
          }
        }}
      />
    </div>
  );
}

/** Confirms what completing will do and captures the next review date and notes. */
function CompleteDialog({
  open,
  review,
  revocations,
  modifications,
  saving,
  onClose,
  onComplete,
}: {
  open: boolean;
  review: AccessReview;
  revocations: AccessReviewItem[];
  modifications: AccessReviewItem[];
  saving: boolean;
  onClose: () => void;
  onComplete: (body: { notes: string; nextReviewOn: string }) => void;
}) {
  const [nextReviewOn, setNextReviewOn] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (!open) return;
    setNextReviewOn(addMonthsIso(localDateIso(), 3));
    setNotes(review.notes);
  }, [open, review.notes]);

  const confirmed = review.counts.confirmed;
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(nextReviewOn);

  return (
    <Dialog open={open} onClose={onClose} title="Complete review">
      <div className="record-dialog">
        <p className="coverage-hint">
          Completing applies every decision to the live grants and cannot be undone: revoked grants
          are ended, changed grants take their new level, and confirmed grants have their next review
          date moved on by their review frequency.
        </p>
        <h3 className="review-summary-title">Will be revoked ({revocations.length})</h3>
        {revocations.length === 0 ? (
          <p className="panel-empty">No grants will be revoked.</p>
        ) : (
          <ul className="review-summary-list">
            {revocations.map((i) => (
              <li key={i.id}>
                <strong>{i.userName}</strong> — {i.recordName}
                {i.recordDeleted && ' (record deleted)'}
              </li>
            ))}
          </ul>
        )}
        <h3 className="review-summary-title">Will be changed ({modifications.length})</h3>
        {modifications.length === 0 ? (
          <p className="panel-empty">No grants will change level.</p>
        ) : (
          <ul className="review-summary-list">
            {modifications.map((i) => (
              <li key={i.id}>
                <strong>{i.userName}</strong> — {i.recordName}: {accessLevelLabel(i.accessLevel)} →{' '}
                {i.newAccessLevel ? ACCESS_LEVEL_LABELS[i.newAccessLevel] : '—'}
              </li>
            ))}
          </ul>
        )}
        <p className="coverage-hint">
          {confirmed} grant{confirmed === 1 ? '' : 's'} confirmed as still needed.
        </p>
        <Field label="Next review" hint="Reviews are typically quarterly" required>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={nextReviewOn}
              onChange={(e) => setNextReviewOn(e.target.value)}
            />
          )}
        </Field>
        <Field label="Notes" hint="Optional — appear in the report">
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={notes}
              maxLength={5000}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!valid}
            loading={saving}
            onClick={() => onComplete({ notes: notes.trim(), nextReviewOn })}
          >
            Complete review
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Add or edit one "other system reviewed" row. */
function SystemDialog({
  open,
  system,
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  system: AccessReviewSystem | null;
  saving: boolean;
  onClose: () => void;
  onSave: (input: UpsertAccessReviewSystemInput) => void;
}) {
  const [name, setName] = useState('');
  const [accountsReviewed, setAccountsReviewed] = useState('');
  const [privilegedRoles, setPrivilegedRoles] = useState('');
  const [changesMade, setChangesMade] = useState('');
  const [outcome, setOutcome] = useState<AccessReviewOutcome>('compliant');
  useEffect(() => {
    if (!open) return;
    setName(system?.system ?? '');
    setAccountsReviewed(system?.accountsReviewed ?? '');
    setPrivilegedRoles(system?.privilegedRoles ?? '');
    setChangesMade(system?.changesMade ?? '');
    setOutcome(system?.outcome ?? 'compliant');
  }, [open, system]);

  return (
    <Dialog open={open} onClose={onClose} title={system ? `Edit ${system.system}` : 'Add a system'}>
      <div className="record-dialog">
        <Field label="System" hint="e.g. Shared mailboxes, Client environments, Physical access (fobs and keys), Banking" required>
          {(props) => (
            <Input {...props} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>
        <Field label="Accounts reviewed" hint="What was checked, e.g. 4 shared mailboxes and their delegates">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={accountsReviewed}
              maxLength={2000}
              onChange={(e) => setAccountsReviewed(e.target.value)}
            />
          )}
        </Field>
        <Field label="Privileged roles" hint="Named privileged roles and who holds them">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={privilegedRoles}
              maxLength={2000}
              onChange={(e) => setPrivilegedRoles(e.target.value)}
            />
          )}
        </Field>
        <Field label="Changes made">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={changesMade}
              maxLength={2000}
              onChange={(e) => setChangesMade(e.target.value)}
            />
          )}
        </Field>
        <Field label="Outcome">
          {(props) => (
            <Select
              {...props}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as AccessReviewOutcome)}
            >
              {ACCESS_REVIEW_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {ACCESS_REVIEW_OUTCOME_LABELS[o]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={name.trim().length === 0}
            loading={saving}
            onClick={() =>
              onSave({
                system: name.trim(),
                accountsReviewed: accountsReviewed.trim(),
                privilegedRoles: privilegedRoles.trim(),
                changesMade: changesMade.trim(),
                outcome,
              })
            }
          >
            {system ? 'Save changes' : 'Add system'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
